import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import {
    Trans,
    useCurrentLanguage,
    useTranslator,
    useUrlLocalizer
} from 'tradera/lang/translate';
import FormHeader, { SwedenCountryCode } from '../common/form/form-header';
import { Heading } from '../common/form/form-parts';
import * as Validator from 'validator';
import { touchWebClient } from 'tradera/utils/http';
import IntlTelInput from 'react-intl-tel-input';
import 'react-intl-tel-input/dist/main.css';
import GoogleTagManager from '../../services/google-tag-manager';
import { GoogleSignupButton } from './google-signup-button';
import EventEmitter from '../../helpers/event-emitter';
import '../../services/form-tracker';
import { getAvailableUserRegistrationCountries } from '../../helpers/get-available-user-registration-countries';
import { FormError } from '../common/form/form-error';
import { KountBlockedMessage } from '../common/kount-blocked-message';
import { FormFooter } from 'tradera/apps/registration/components/common/form/form-footer';
import qs from 'qs';
import { useDispatch, useSelector } from 'react-redux';
import { getGeolocation } from 'tradera/state/member/actions';
import { selectGeolocation } from 'tradera/state/member/selectors';
import { FormCompleted } from 'tradera/apps/registration/components/common/form/form-completed';
import {
    getAcceptsMarketing,
    setAcceptsMarketing,
    triggerUserSignedUpEvent
} from 'tradera/apps/registration/helpers/blueshift';
import BackIcon from '@tradera/blueprint/theme/icons2/back.svg?jsx';
import useIsMobile from 'tradera/hooks/use-is-mobile';
import dayjs from 'dayjs';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import localeData from 'dayjs/plugin/localeData';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { SelectSocialSignup } from '../common/form/select-social-signup';
import Apple from '../../helpers/social-auth/apple';
import Google from '../../helpers/social-auth/google';
import Facebook from '../../helpers/social-auth/facebook';
import Klarna, {
    loadSignInWithKlarna
} from '../../helpers/social-auth/klarna2';
import BlockingLoader from 'tradera/apps/registration/components/common/form/blocking-loader';
import * as ga from '../../services/login-signup-analytics';
import {
    PROVIDER_NAMES,
    PROVIDERS
} from 'tradera/apps/registration/helpers/social-auth/providers';
import CheckBadgeIcon from '@tradera/blueprint/theme/icons2/check-badge.svg?jsx';
import { SelectCountry } from '../common/form/select-country';
import { Button } from 'tradera/components/atoms/button/button';
import { waitForCountSessionId } from 'tradera/apps/registration/components/common/kount-session-id-awaiter';
import { InfoTooltip } from 'tradera/components/info-tooltip/info-tooltip';
import {
    isDenmarkCountryCode,
    isNetherlandsCountryCode
} from 'tradera/localization/countrycode';
import { getLoginLocaleFromLanguage } from 'tradera/legacy/login/script/app/ui/get-login-locale-from-language';
import { DateOfBirthInput } from 'tradera/apps/registration/components/forms/date-of-birth-input';

const views = {
    Initial: 'Initial',

    // Email
    AdditionalDetails: 'AdditionalDetails',
    AccountCreated: 'AccountCreated',

    // Social
    AdditionalDetailsSocial: 'AdditionalDetailsSocial',
    AccountExistsSocial: 'AccountExistsSocial',
    AccountPendingSocial: 'AccountPendingSocial',
    ManualReviewSocial: 'ManualReviewSocial',
    AccountApprovedSocial: 'AccountApprovedSocial',

    // Only Facebook?
    EmailMissingSocial: 'EmailMissingSocial'
};

dayjs.extend(localizedFormat);
dayjs.extend(localeData);
dayjs.extend(customParseFormat);

export const ForeignPrivateForm = ({
    antiForgeryToken,
    appleEnabled,
    appleAppId,
    facebookEnabled,
    facebookAppId,
    googleEnabled,
    googleAppId,
    klarnaEnabled: klarnaForeignEnabled,
    klarnaAppId,
    klarnaEnvironment,
    isNativeAppContext
}) => {
    const { t } = useTranslator();
    const { toLocalizedUrl } = useUrlLocalizer();
    const availableCountries = getAvailableUserRegistrationCountries(t);
    const isMobile = useIsMobile();
    const currentLanguage = useCurrentLanguage();
    const currentLocale = getLoginLocaleFromLanguage(currentLanguage);
    dayjs.locale(currentLocale);

    useEffect(() => {
        EventEmitter.dispatch('Form:Loaded', {
            name: 'foreign-private',
            step: 1
        });
    }, []);

    const {
        register,
        handleSubmit,
        formState: { errors },
        control,
        trigger,
        setValue,
        getValues,
        resetField
    } = useForm({
        mode: 'onBlur'
    });

    const [successfulSubmissionData, setSuccessfulSubmissionData] =
        useState(null);
    const [serverErrors, setServerErrors] = useState({});
    const [currentView, setCurrentView] = useState(views.Initial);

    const dispatch = useDispatch();
    useEffect(() => {
        dispatch(getGeolocation());
    }, [dispatch]);
    const geolocation = useSelector(selectGeolocation);
    const isoCode = geolocation?.isoCode;
    let defaultCountryCode =
        qs.parse(window.location.search, {
            ignoreQueryPrefix: true
        }).countryCode || isoCode;
    if (
        defaultCountryCode &&
        (defaultCountryCode.toLowerCase() === SwedenCountryCode ||
            !availableCountries.find(
                (a) => a.code === defaultCountryCode.toLowerCase()
            ))
    ) {
        defaultCountryCode = availableCountries[1].code;
    }
    const [countryCode, setCountryCode] = useState(defaultCountryCode);
    useEffect(() => setCountryCode(defaultCountryCode), [defaultCountryCode]);

    const klarnaEnabled =
        klarnaForeignEnabled &&
        (isDenmarkCountryCode(countryCode) ||
            isNetherlandsCountryCode(countryCode));

    const validateAsync = useCallback(
        async (url, value) => {
            try {
                await touchWebClient.post(
                    url,
                    {
                        value: value.trim()
                    },
                    {
                        headers: {
                            ['X-Xsrf-Token']: antiForgeryToken
                        }
                    }
                );

                return true;
            } catch (err) {
                if (
                    err.response &&
                    err.response.data &&
                    err.response.status === 400
                ) {
                    return { success: false, error: err.response.data };
                }

                throw err;
            }
        },
        [antiForgeryToken]
    );

    const setServerValidationError = (error, field) => {
        if (error) {
            setServerErrors({
                ...serverErrors,
                [field]: error.code
            });
        } else if (!error) {
            const _serverErrors = { ...serverErrors };
            delete _serverErrors[field];
            setServerErrors(_serverErrors);
        }
    };

    const validateEmail = async (v) => {
        const { success, error } = await validateAsync(
            '/register/api/validate/email',
            v
        );

        setServerValidationError(error, 'emailAddress');
        return success;
    };

    const validateAlias = async (v) => {
        const { success, error } = await validateAsync(
            '/register/api/validate/alias',
            v
        );

        setServerValidationError(error, 'alias');
        return success;
    };

    const onSubmit = async (data) => {
        if (currentView === views.AdditionalDetailsSocial) {
            setCurrentView(views.AccountPendingSocial);
        }
        try {
            const sessionId = await waitForCountSessionId();
            const result = await touchWebClient.post(
                '/api/register/foreign-private',
                {
                    ...data,
                    phoneNumber: data.phoneNumber.number,
                    dateOfBirth: data.dateOfBirth.format('YYYY-MM-DD'),
                    countryCodeIso2: countryCode,
                    sessionId
                },
                {
                    headers: {
                        ['X-Xsrf-Token']: antiForgeryToken
                    }
                }
            );

            window.location.hash = 'confirm';
            EventEmitter.dispatch('Form:Submit:Success', result.data);

            GoogleTagManager.pushToDataLayer({
                event: 'user-signup-success',
                'member-id': result.data.memberId
            });
            GoogleTagManager.trackEvent({
                category: 'Login and Signup',
                action: 'Signup confirm - Foreign private'
            });
            setSuccessfulSubmissionData({
                memberId: result.data.memberId,
                emailAddress: result.data.emailAddress,
                redirectLink: result.data.redirectLink
            });
            if (currentView !== views.AdditionalDetailsSocial) {
                setCurrentView(views.AccountCreated);
            }
            triggerUserSignedUpEvent(
                result.data.memberId,
                result.data.emailAddress
            );
        } catch (err) {
            if (err.response && err.response.data && err.response.data.errors) {
                setServerErrors(
                    err.response.data.errors.reduce((serverErrors, error) => {
                        serverErrors[error.field] = error.code;
                        return serverErrors;
                    }, {})
                );
                EventEmitter.dispatch('Form:Submit:Failure', {
                    errors: err.response.data.errors
                });
                // currentView has not yet been changed so set it back to additionalDetailsSocial on errors
                if (currentView === views.AdditionalDetailsSocial) {
                    setCurrentView(views.AdditionalDetailsSocial);
                }
            }
        }
    };

    const [isLoading, setIsLoading] = useState(false);

    const processToNextStep = useCallback(
        async (provider, providerData) => {
            if (!provider.isApiLoaded() && !isNativeAppContext) {
                provider.setNotLoaded();
                ga.trackEvent(`user-signup-error`);
                return;
            }
            ga.method.set(provider.gaMethod);
            ga.trackEvent(`user-signup-step-2`);
            setIsLoading(!!provider.useLoadingSpinner);
            try {
                const data = (await provider.login({
                    native: isNativeAppContext,
                    providerData
                })) || { accessToken: '', email: '', firstName: '' };

                if (!data.email) {
                    setIsLoading(false);
                    setCurrentView(views.EmailMissingSocial);
                    ga.trackEvent('user-signup-fail', 0, 'email-is-required');
                    return;
                }

                provider.setValues(data);

                const validateUserIdResult = await validateAsync(
                    `/register/api/validate/${provider.providerType}-id`,
                    data.userId
                );
                if (
                    !validateUserIdResult.success &&
                    validateUserIdResult.error?.code ===
                        `${provider.providerType.toUpperCase()}_ID_IN_USE`
                ) {
                    try {
                        const res = await provider.loginOnTradera();
                        ga.trackEvent(
                            'user-login-id-in-use-login-success',
                            res && res.data && res.data.memberId
                        );
                        setSuccessfulSubmissionData({
                            memberId: res.data.memberId,
                            emailAddress: getValues('emailAddress'),
                            redirectLink: res.data.returnUrl
                        });
                        setCurrentView(views.AccountExistsSocial);
                    } catch (err) {
                        console.error(
                            `/login/${provider.providerType}/verify failed`,
                            err
                        );
                        // Detta borde inte kunna ske, kontakta cs/försök igen
                    } finally {
                        setIsLoading(false);
                    }
                    return;
                }

                const validateEmailResult = await validateAsync(
                    '/register/api/validate/email',
                    data.email
                );

                if (
                    !validateEmailResult.success &&
                    validateEmailResult.error?.code === 'EMAIL_IN_USE'
                ) {
                    try {
                        // This should always fail at this point, but is needed for connect flow
                        await provider.loginOnTradera();
                    } finally {
                        ga.trackEvent(`user-login-email-in-use`, null);
                        window.location.href = toLocalizedUrl(
                            `/login?connect=${provider.providerType}&connectmatch=email`
                        );
                    }
                    return;
                }

                setCurrentView(views.AdditionalDetailsSocial);

                setIsLoading(false);
                ga.trackEvent(`user-signup-step-3`);
            } catch (err) {
                console.error(
                    `Failed logging in to ${provider.providerTypeName}`,
                    err
                );
            } finally {
                setIsLoading(false);
            }
        },
        [getValues, isNativeAppContext, toLocalizedUrl, validateAsync]
    );

    useEffect(() => {
        if (klarnaEnabled) {
            return loadSignInWithKlarna(
                klarnaAppId,
                klarnaEnvironment,
                countryCode?.toUpperCase(),
                currentLocale
            );
        }
    }, [
        countryCode,
        klarnaAppId,
        klarnaEnabled,
        klarnaEnvironment,
        currentLocale
    ]);

    const allSocialProviders = useMemo(() => {
        const providers = [];
        if (googleEnabled) {
            providers.push(
                (() => {
                    const google = new Google(googleAppId);
                    let notLoaded = false;

                    var provider = {
                        isApiLoaded: () => google.isLoaded(),
                        setNotLoaded: () => {
                            // eslint-disable-next-line better-mutation/no-mutation
                            notLoaded = true;
                        },
                        getNotLoaded: () => notLoaded,
                        providerType: PROVIDERS.GOOGLE,
                        providerName: PROVIDER_NAMES[PROVIDERS.GOOGLE],
                        gaMethod: ga.METHOD.GOOGLE,
                        login: (options) => google.login(options),
                        setValues: (data) => {
                            setValue('emailAddress', data.email);
                            setValue('firstName', data.firstName);
                            setValue('lastName', data.lastName);
                            setValue('googleUserAccessToken', data.accessToken);
                        },
                        loginOnTradera: () =>
                            touchWebClient.post('/login/google/verify', {
                                AccessToken: getValues('googleUserAccessToken'),
                                UserId: 'NOT_IN_USE'
                            }),
                        reset: () => {
                            resetField('googleUserAccessToken');
                        },
                        isCurrentProvider: () =>
                            !!getValues('googleUserAccessToken'),
                        useLoadingSpinner: true
                    };

                    provider.customButton = !isNativeAppContext ? (
                        <GoogleSignupButton
                            googleAppId={googleAppId}
                            onCallback={(data) =>
                                processToNextStep(provider, data)
                            }
                        />
                    ) : undefined;

                    return provider;
                })()
            );
        }

        if (klarnaEnabled)
            providers.push(
                (() => {
                    const klarna = new Klarna();

                    let notLoaded = false;
                    return {
                        isApiLoaded: () => klarna.isLoaded(),
                        setNotLoaded: () => {
                            // eslint-disable-next-line better-mutation/no-mutation
                            notLoaded = true;
                        },
                        getNotLoaded: () => notLoaded,
                        providerType: PROVIDERS.KLARNA,
                        providerName: PROVIDER_NAMES[PROVIDERS.KLARNA],
                        gaMethod: ga.METHOD.KLARNA,
                        login: (options) => klarna.login(options),
                        setValues: (data) => {
                            setValue('emailAddress', data.email);
                            setValue('firstName', data.firstName);
                            setValue('lastName', data.lastName);
                            // We do sometimes get a date of birth, but it doesn't match
                            // our validation format. So we avoid using it for now.
                            // setValue('dateOfBirth', data.dateOfBirth);
                            setValue('streetName', data.streetName);
                            setValue('zipCode', data.zipCode);
                            setValue('town', data.town);
                            // We do have a phone number, but our validation doesn't
                            // accept the country code. So we avoid using it for now.
                            // setValue('phoneNumber', data.phoneNumber);
                            setValue('klarnaId', data.klarnaId);
                            setValue('klarnaIdToken', data.idToken);
                            setValue('klarnaRefreshToken', data.refreshToken);
                        },
                        loginOnTradera: () =>
                            touchWebClient.post('/login/klarna/verify', {
                                AccessToken: getValues('klarnaIdToken'),
                                UserId: getValues('klarnaId')
                            }),
                        reset: () => {
                            resetField('klarnaId');
                            resetField('klarnaIdToken');
                            resetField('klarnaRefreshToken');
                        },
                        isCurrentProvider: () => !!getValues('klarnaIdToken'),
                        useLoadingSpinner: false
                    };
                })()
            );

        if (appleEnabled)
            providers.push(
                (() => {
                    const apple = new Apple(appleAppId);
                    let notLoaded = false;
                    return {
                        isApiLoaded: () => apple.isLoaded(),
                        setNotLoaded: () => {
                            // eslint-disable-next-line better-mutation/no-mutation
                            notLoaded = true;
                        },
                        getNotLoaded: () => notLoaded,
                        providerType: PROVIDERS.APPLE,
                        providerName: PROVIDER_NAMES[PROVIDERS.APPLE],
                        gaMethod: ga.METHOD.APPLE,
                        login: (options) => apple.login(options),
                        setValues: (data) => {
                            setValue('emailAddress', data.email);
                            setValue('firstName', data.firstName);
                            setValue('lastName', data.lastName);
                            setValue('appleUserAccessToken', data.accessToken);
                        },
                        loginOnTradera: () =>
                            touchWebClient.post('/login/apple/verify', {
                                AccessToken: getValues('appleUserAccessToken'),
                                UserId: 'NOT_IN_USE'
                            }),
                        reset: () => {
                            resetField('appleUserAccessToken');
                        },
                        isCurrentProvider: () =>
                            !!getValues('appleUserAccessToken'),
                        useLoadingSpinner: true
                    };
                })()
            );

        if (facebookEnabled)
            providers.push(
                (() => {
                    const facebook = new Facebook(facebookAppId);
                    let notLoaded = false;
                    return {
                        isApiLoaded: () => facebook.isLoaded(),
                        setNotLoaded: () => {
                            // eslint-disable-next-line better-mutation/no-mutation
                            notLoaded = true;
                        },
                        getNotLoaded: () => notLoaded,
                        providerType: PROVIDERS.FACEBOOK,
                        providerName: PROVIDER_NAMES[PROVIDERS.FACEBOOK],
                        gaMethod: ga.METHOD.FACEBOOK,
                        login: (options) => facebook.login(options),
                        setValues: (data) => {
                            setValue('emailAddress', data.email);
                            setValue('firstName', data.firstName);
                            setValue('lastName', data.lastName);
                            setValue('facebookId', data.userId);
                            setValue(
                                'facebookUserAccessToken',
                                data.accessToken
                            );
                        },
                        loginOnTradera: () =>
                            touchWebClient.post('/login/facebook/verify', {
                                AccessToken: getValues(
                                    'facebookUserAccessToken'
                                ),
                                UserId: getValues('facebookId')
                            }),
                        reset: () => {
                            resetField('facebookId');
                            resetField('facebookUserAccessToken');
                        },
                        isCurrentProvider: () =>
                            !!getValues('facebookUserAccessToken'),
                        useLoadingSpinner: true
                    };
                })()
            );

        return providers;
    }, [
        googleEnabled,
        klarnaEnabled,
        appleEnabled,
        facebookEnabled,
        googleAppId,
        isNativeAppContext,
        setValue,
        getValues,
        resetField,
        processToNextStep,
        appleAppId,
        facebookAppId
    ]);

    const getCurrentSocialProvider = useCallback(() => {
        return allSocialProviders.find((x) => x.isCurrentProvider()) || null;
    }, [allSocialProviders]);

    useEffect(() => {
        if (
            currentView !== views.AccountPendingSocial ||
            !successfulSubmissionData?.memberId
        ) {
            return;
        }

        let currentTimeoutId = null;
        const poll = () =>
            new Promise((resolve) => {
                const call = () => {
                    // eslint-disable-next-line better-mutation/no-mutation
                    currentTimeoutId = setTimeout(async () => {
                        try {
                            const response = await touchWebClient.get(
                                `/register/api/account/status/${successfulSubmissionData?.memberId}`
                            );
                            resolve(response);
                            switch (response.data) {
                                case 'Approved':
                                    {
                                        const successData =
                                            successfulSubmissionData || {};
                                        ga.trackEvent(
                                            'user-signup-success',
                                            successData.memberId
                                        );
                                        triggerUserSignedUpEvent(
                                            successData.memberId,
                                            successData.emailAddress
                                        );

                                        const provider =
                                            getCurrentSocialProvider();
                                        provider.loginOnTradera();
                                        setCurrentView(
                                            views.AccountApprovedSocial
                                        );
                                    }
                                    break;
                                case 'ManualReview':
                                    setCurrentView(views.ManualReviewSocial);
                                    break;
                                case 'Pending':
                                    break;
                                default:
                                    setCurrentView(views.ManualReviewSocial);
                            }
                        } catch (error) {
                            setCurrentView(views.ManualReviewSocial);
                        } finally {
                            if (currentTimeoutId) call();
                        }
                    }, 500);
                };
                call();
            });

        poll();
        return () => {
            clearTimeout(currentTimeoutId);
            // eslint-disable-next-line better-mutation/no-mutation
            currentTimeoutId = null;
        };
    }, [
        currentView,
        getCurrentSocialProvider,
        getValues,
        successfulSubmissionData,
        successfulSubmissionData?.memberId
    ]);

    const onSocialStart = useCallback(
        (provider) => async (e) => {
            e.preventDefault();
            await processToNextStep(provider);
        },
        [processToNextStep]
    );

    const invalidFieldClassName = 'is-invalid';

    const onBackClicked = useCallback(() => {
        getCurrentSocialProvider()?.reset();
        setCurrentView(views.Initial);
    }, [getCurrentSocialProvider]);

    useEffect(() => {
        if (currentView !== views.Initial) {
            return;
        }
        ga.method.set(ga.METHOD.UNKNOWN);
        ga.flow.set(ga.FLOW.SIGNUP_FOREIGN);
        ga.trackEvent('user-signup-step-1');
    }, [currentView]);

    return (
        <>
            {(currentView === views.Initial ||
                currentView === views.EmailMissingSocial) && (
                <Initial
                    availableCountries={availableCountries}
                    countryCode={countryCode}
                    errors={errors}
                    invalidFieldClassName={invalidFieldClassName}
                    onComplete={() => {
                        setCurrentView(views.AdditionalDetails);
                        ga.method.set(ga.METHOD.PASSWORD);
                    }}
                    allSocialProviders={allSocialProviders}
                    googleAppId={googleAppId}
                    onSocialStart={onSocialStart}
                    register={register}
                    serverErrors={serverErrors}
                    setCountryCode={setCountryCode}
                    showEmailMissingInfo={
                        currentView === views.EmailMissingSocial
                    }
                    trigger={trigger}
                    validateEmail={validateEmail}
                />
            )}
            {(currentView === views.AdditionalDetails ||
                currentView === views.AdditionalDetailsSocial) && (
                <AdditionalDetails
                    control={control}
                    countryCode={countryCode}
                    errors={errors}
                    handleSubmit={handleSubmit}
                    invalidFieldClassName={invalidFieldClassName}
                    isMobile={isMobile}
                    onBackClicked={onBackClicked}
                    onSubmit={onSubmit}
                    register={register}
                    serverErrors={serverErrors}
                    validateAlias={validateAlias}
                />
            )}
            {currentView === views.AccountPendingSocial && (
                <AccountPendingSocial />
            )}
            {currentView === views.AccountExistsSocial && (
                <AccountExistsSocial
                    isNativeAppContext={isNativeAppContext}
                    provider={PROVIDERS.GOOGLE}
                    redirectLink={successfulSubmissionData.redirectLink}
                />
            )}
            {currentView === views.AccountApprovedSocial && (
                <AccountApprovedSocial
                    isNativeAppContext={isNativeAppContext}
                    providerType={getCurrentSocialProvider().providerType}
                    redirectLink={successfulSubmissionData.redirectLink}
                />
            )}
            {currentView === views.ManualReviewSocial && (
                <ManualReviewSocial
                    emailAddress={successfulSubmissionData.emailAddress}
                    redirectLink={successfulSubmissionData.redirectLink}
                />
            )}
            {currentView === views.AccountCreated && (
                <AccountCreated
                    emailAddress={successfulSubmissionData.emailAddress}
                    redirectLink={successfulSubmissionData.redirectLink}
                />
            )}
            <FormFooter
                registerLink={toLocalizedUrl('/register/foreign-company')}
                isCompanyLink={true}
                logoOnly={
                    ![
                        views.Initial,
                        views.AdditionalDetails,
                        views.AdditionalDetailsSocial,
                        views.EmailMissingSocial
                    ].includes(currentView)
                }
            />
            {isLoading && <BlockingLoader />}
        </>
    );
};

const Initial = ({
    allSocialProviders,
    availableCountries,
    countryCode,
    errors,
    invalidFieldClassName,
    onComplete,
    onSocialStart,
    register,
    serverErrors,
    setCountryCode,
    showEmailMissingInfo,
    trigger,
    validateEmail
}) => {
    const { t } = useTranslator();
    return (
        <>
            <FormHeader showLanguageMenu={true} />
            {countryCode && (
                <div>
                    <KountBlockedMessage />
                    <Heading>
                        {showEmailMissingInfo
                            ? t('registration_sign_up_email_missing_heading')
                            : t('registration_fp_sign_up_heading')}
                    </Heading>
                    <div className={'text-oslo mb-3 text-center'}>
                        {showEmailMissingInfo
                            ? t('registration_sign_up_email_missing_text')
                            : t('registration_sign_up_welcome_to')}
                    </div>
                    <SelectCountry
                        availableCountries={availableCountries}
                        onCountrySelected={(selectedValue) => {
                            if (selectedValue === SwedenCountryCode) {
                                window.location.href = '/register';
                            } else {
                                setCountryCode(selectedValue);
                            }
                        }}
                        selectedCountryCodeIso2={countryCode}
                    />
                    {allSocialProviders.length > 0 && (
                        <div className="border-bottom mb-4 position-relative">
                            <SelectSocialSignup
                                allSocialProviders={allSocialProviders}
                                onSocialStart={onSocialStart}
                            />
                            <div
                                className="text-center position-absolute"
                                style={{
                                    transform: 'translate(-50%, 50%)',
                                    bottom: 0,
                                    left: '50%'
                                }}>
                                <span className="size-paris text-styled font-weight-bold bg-white pill px-3">
                                    {t('registration_sign_up_or')}
                                </span>
                            </div>
                        </div>
                    )}
                    <div className="mb-1">
                        <input
                            placeholder={t('registration_placeholder_email')}
                            type="email"
                            className={`form-control px-1 py-2 ${
                                errors.emailAddress ? invalidFieldClassName : ''
                            }`}
                            {...register('emailAddress', {
                                required: t(
                                    'registration_validation_required_email'
                                ),
                                minLength: {
                                    value: 5,
                                    message: t(
                                        'registration_validation_invalid_length_email'
                                    )
                                },
                                maxLength: {
                                    value: 255,
                                    message: t(
                                        'registration_validation_maxlength_email'
                                    )
                                },
                                validate: {
                                    isValid: (v) =>
                                        Validator.isEmail(v)
                                            ? true
                                            : t(
                                                  'registration_validation_invalid_email'
                                              ),
                                    validateEmail: (v) => validateEmail(v, t)
                                }
                            })}
                        />
                        <FormError
                            serverErrors={serverErrors}
                            errors={errors}
                            name="emailAddress"
                        />
                    </div>
                    <Button
                        noTransform
                        theme="primary"
                        fluid
                        type="button"
                        onClick={async () => {
                            const result = await trigger(['emailAddress']);

                            if (result) {
                                onComplete();
                            }
                        }}>
                        <strong>{t('registration_sign_up_continue')}</strong>
                    </Button>
                </div>
            )}
        </>
    );
};

const AdditionalDetails = ({
    control,
    countryCode,
    errors,
    handleSubmit,
    invalidFieldClassName,
    isMobile,
    onBackClicked,
    onSubmit,
    register,
    serverErrors,
    validateAlias
}) => {
    const { t } = useTranslator();
    return (
        <>
            <div>
                <Button
                    linkReverted
                    theme="link"
                    className="text-inter-medium size-london"
                    type="submit"
                    onClick={onBackClicked}
                    icon={<BackIcon />}>
                    {!isMobile && t('registration_sign_up_back')}
                </Button>
            </div>
            {countryCode && (
                <>
                    <KountBlockedMessage />
                    <form onSubmit={handleSubmit(onSubmit)}>
                        <Heading className="mb-6">
                            {t('registration_sign_up_heading_soon_done')}
                        </Heading>

                        <div className="mb-1 d-flex align-items-center justify-content-between position-relative">
                            <span className="font-hansen">
                                {t('registration_validation_title_details')}
                            </span>

                            <InfoTooltip
                                iconSize="sm"
                                containerClassName="ml-1">
                                {t('registration_tooltip_phone_number')}
                            </InfoTooltip>
                        </div>
                        <div className="d-flex w-100 mb-1">
                            <div className="mr-1 w-50">
                                <input
                                    type="text"
                                    className={`form-control px-1 py-2 ${
                                        errors.firstName
                                            ? invalidFieldClassName
                                            : ''
                                    }`}
                                    placeholder={t(
                                        'registration_placeholder_first_name'
                                    )}
                                    {...register('firstName', {
                                        required: t(
                                            'registration_validation_required_firstname'
                                        ),
                                        minLength: {
                                            value: 2,
                                            message: t(
                                                'registration_validation_minlength_firstname'
                                            )
                                        },
                                        maxLength: {
                                            value: 30,
                                            message: t(
                                                'registration_validation_maxlength_firstname'
                                            )
                                        },
                                        pattern: {
                                            value: /^[\u0041-\u024F -:]+$/,
                                            message: t(
                                                'registration_validation_invalid_characters'
                                            )
                                        }
                                    })}
                                />

                                <FormError
                                    serverErrors={serverErrors}
                                    errors={errors}
                                    name="firstName"
                                />
                            </div>
                            <div className="w-50">
                                <input
                                    type="text"
                                    className={`form-control px-1 py-2 ${
                                        errors.lastName
                                            ? invalidFieldClassName
                                            : ''
                                    }`}
                                    placeholder={t(
                                        'registration_placeholder_last_name'
                                    )}
                                    {...register('lastName', {
                                        required: t(
                                            'registration_validation_required_lastname'
                                        ),
                                        minLength: {
                                            value: 2,
                                            message: t(
                                                'registration_validation_minlength_lastname'
                                            )
                                        },
                                        maxLength: {
                                            value: 30,
                                            message: t(
                                                'registration_validation_maxlength_lastname'
                                            )
                                        },
                                        pattern: {
                                            value: /^[\u0041-\u024F -:]+$/,
                                            message: t(
                                                'registration_validation_invalid_characters'
                                            )
                                        }
                                    })}
                                />
                                <FormError
                                    serverErrors={serverErrors}
                                    errors={errors}
                                    name="lastName"
                                />
                            </div>
                        </div>

                        <div className="mb-1">
                            <input
                                placeholder={t(
                                    'registration_placeholder_street'
                                )}
                                className={`form-control px-1 py-2 ${
                                    errors.streetName
                                        ? invalidFieldClassName
                                        : ''
                                }`}
                                {...register('streetName', {
                                    required: t(
                                        'registration_validation_required_streetname'
                                    ),
                                    minLength: {
                                        value: 5,
                                        message: t(
                                            'registration_validation_minlength_streetname'
                                        )
                                    },
                                    maxLength: {
                                        value: 50,
                                        message: t(
                                            'registration_validation_maxlength_streetname'
                                        )
                                    },
                                    pattern: {
                                        value: /^[0-9\u0041-\u024F -:]+$/,
                                        message: t(
                                            'registration_validation_invalid_characters'
                                        )
                                    }
                                })}
                            />
                            <FormError
                                serverErrors={serverErrors}
                                errors={errors}
                                name="streetName"
                            />
                        </div>

                        <div className="d-flex w-100 mb-1">
                            <div className="mr-1 w-50">
                                <input
                                    type="tel"
                                    className={`form-control px-1 py-2 ${
                                        errors.zipCode
                                            ? invalidFieldClassName
                                            : ''
                                    }`}
                                    placeholder={t(
                                        'registration_placeholder_zip_code'
                                    )}
                                    {...register('zipCode', {
                                        required: t(
                                            'registration_validation_required_zipcode'
                                        ),
                                        minLength: {
                                            value: 4,
                                            message: t(
                                                'registration_validation_invalid_zipcode'
                                            )
                                        },
                                        maxLength: {
                                            value: 8,
                                            message: t(
                                                'registration_validation_invalid_zipcode'
                                            )
                                        },
                                        pattern: {
                                            value: /^[A-Za-z0-9\- ]{3,8}$/,
                                            message: t(
                                                'registration_validation_invalid_zipcode'
                                            )
                                        }
                                    })}
                                />

                                <FormError
                                    serverErrors={serverErrors}
                                    errors={errors}
                                    name="zipCode"
                                />
                            </div>
                            <div className="w-50">
                                <input
                                    type="text"
                                    className={`form-control px-1 py-2 ${
                                        errors.town ? invalidFieldClassName : ''
                                    }`}
                                    placeholder={t(
                                        'registration_placeholder_town'
                                    )}
                                    {...register('town', {
                                        required: t(
                                            'registration_validation_required_town'
                                        ),
                                        minLength: {
                                            value: 2,
                                            message: t(
                                                'registration_validation_minlength_town'
                                            )
                                        },
                                        maxLength: {
                                            value: 36,
                                            message: t(
                                                'registration_validation_maxlength_town'
                                            )
                                        },
                                        pattern: {
                                            value: /^[\u0041-\u024F -:]+$/,
                                            message: t(
                                                'registration_validation_invalid_characters'
                                            )
                                        }
                                    })}
                                />
                                <FormError
                                    serverErrors={serverErrors}
                                    errors={errors}
                                    name="town"
                                />
                            </div>
                        </div>

                        <div className="mb-4">
                            <div className="d-flex align-items-center position-relative">
                                <Controller
                                    name="phoneNumber"
                                    control={control}
                                    rules={{
                                        required: t(
                                            'registration_validation_required_phonenumber'
                                        ),
                                        validate: ({ status }) =>
                                            status ||
                                            t(
                                                'registration_validation_invalid_phonenumber'
                                            )
                                    }}
                                    render={({
                                        field: { onChange, onBlur, name }
                                    }) => (
                                        <IntlTelInput
                                            fieldName={name}
                                            containerClassName="intl-tel-input"
                                            inputClassName={`form-control ${
                                                errors.phoneNumber
                                                    ? invalidFieldClassName
                                                    : ''
                                            }`}
                                            onPhoneNumberChange={(
                                                status,
                                                _value,
                                                _countryData,
                                                number
                                            ) => {
                                                onChange({
                                                    number,
                                                    status
                                                });
                                            }}
                                            onPhoneNumberBlur={(
                                                status,
                                                _value,
                                                _countryData,
                                                number
                                            ) => {
                                                onBlur({
                                                    number,
                                                    status
                                                });
                                            }}
                                            preferredCountries={[
                                                'dk',
                                                'de',
                                                'no',
                                                'fi',
                                                'nl',
                                                'fr'
                                            ]}
                                            excludeCountries={[
                                                'cu',
                                                'ir',
                                                'kp',
                                                'sy'
                                            ]}
                                            format={true}
                                            autoHideDialCode={false}
                                            defaultCountry={countryCode}
                                        />
                                    )}
                                />
                            </div>
                            <FormError
                                serverErrors={serverErrors}
                                errors={errors}
                                name="phoneNumber"
                            />
                        </div>

                        <div className="mt-3 mb-1 d-flex align-items-center justify-content-between position-relative">
                            <span className="font-hansen">
                                {t(
                                    'registration_validation_title_date_of_birth'
                                )}
                            </span>

                            <InfoTooltip
                                iconSize="sm"
                                containerClassName="ml-1">
                                {t('registration_tooltip_date_of_birth')}
                            </InfoTooltip>
                        </div>
                        <div className="mb-1">
                            <div className="d-flex align-items-center position-relative">
                                <Controller
                                    name="dateOfBirth"
                                    control={control}
                                    rules={{
                                        required: t(
                                            'registration_validation_invalid_date_of_birth'
                                        ),
                                        validate: (dateOfBirth) => {
                                            const isValidDAte =
                                                dateOfBirth &&
                                                dateOfBirth.isValid() &&
                                                dateOfBirth.isBefore(dayjs()) &&
                                                dateOfBirth.isAfter(
                                                    dayjs('1900-01-01')
                                                );

                                            return (
                                                isValidDAte ||
                                                t(
                                                    'registration_validation_invalid_date_of_birth'
                                                )
                                            );
                                        }
                                    }}
                                    render={({ field: { onChange } }) => (
                                        <DateOfBirthInput
                                            onChange={onChange}
                                            className={
                                                errors.dateOfBirth
                                                    ? invalidFieldClassName
                                                    : ''
                                            }
                                        />
                                    )}
                                />
                            </div>
                            <FormError
                                serverErrors={serverErrors}
                                errors={errors}
                                name="dateOfBirth"
                            />
                        </div>

                        <div className="mt-3 mb-1">
                            <span className="font-hansen">
                                {t('registration_validation_title_username')}
                            </span>
                        </div>
                        <div className="mb-3">
                            <input
                                placeholder={t(
                                    'registration_placeholder_username'
                                )}
                                type="text"
                                className={`form-control px-1 py-2 ${
                                    errors.alias ? invalidFieldClassName : ''
                                }`}
                                {...register('alias', {
                                    required: t(
                                        'registration_validation_required_alias'
                                    ),
                                    minLength: {
                                        value: 2,
                                        message: t(
                                            'registration_validation_minlength_alias'
                                        )
                                    },
                                    maxLength: {
                                        value: 50,
                                        message: t(
                                            'registration_validation_maxlength_alias'
                                        )
                                    },
                                    pattern: {
                                        value: /^[0-9\u0041-\u024F -:]+$/,
                                        message: t(
                                            'registration_validation_invalid_characters'
                                        )
                                    },
                                    validate: validateAlias
                                })}
                            />
                            <FormError
                                serverErrors={serverErrors}
                                errors={errors}
                                name="alias"
                            />
                        </div>

                        <div className="mb-1">
                            <div className="form-check mb-0 align-items-center">
                                <input
                                    id="terms"
                                    type="checkbox"
                                    className={`form-check-input position-relative registration-checkbox mr-1 ${
                                        errors.terms
                                            ? invalidFieldClassName
                                            : ''
                                    }`}
                                    {...register('terms', {
                                        required: t(
                                            'registration_validation_required_terms'
                                        )
                                    })}
                                />
                                <label htmlFor="terms" className="mb-0">
                                    <Trans
                                        i18nKey="registration_terms_label_Trans"
                                        components={{
                                            a: (
                                                // eslint-disable-next-line jsx-a11y/anchor-is-valid, jsx-a11y/anchor-has-content
                                                <a
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="link"
                                                />
                                            )
                                        }}
                                    />
                                </label>
                            </div>
                            <FormError
                                serverErrors={serverErrors}
                                errors={errors}
                                name="terms"
                            />
                        </div>

                        <div className="mb-3">
                            <div className="form-check mb-0 align-items-center">
                                <input
                                    id="marketing"
                                    type="checkbox"
                                    className={`form-check-input position-relative registration-checkbox mr-1`}
                                    onChange={(e) =>
                                        setAcceptsMarketing(e.target.checked)
                                    }
                                    defaultChecked={getAcceptsMarketing()}
                                    {...register('marketing', {})}
                                />
                                <label htmlFor="marketing" className="mb-0">
                                    {t('registration_marketing_label')}
                                </label>
                            </div>
                            <FormError
                                serverErrors={serverErrors}
                                errors={errors}
                                name="marketing"
                            />
                        </div>

                        <div className="mt-2">
                            <Button
                                noTransform
                                theme="primary"
                                fluid
                                type="submit">
                                <strong>
                                    {t('registration_sign_up_button')}
                                </strong>
                            </Button>

                            <FormError
                                serverErrors={serverErrors}
                                errors={errors}
                                name="countryCodeIso2"
                            />
                        </div>
                    </form>
                </>
            )}
        </>
    );
};

const AccountPendingSocial = () => {
    const { t } = useTranslator();
    return (
        <>
            <div />
            <div className={'mt-7'}>
                <Heading>{t('registration_sign_up_creating')}</Heading>
                <div className="blocking-loader-container mb-5">
                    <BlockingLoader />
                </div>
                <div className={'text-center mb-3'}>
                    <Trans
                        i18nKey="registration_sign_up_problems_Trans"
                        // eslint-disable-next-line jsx-a11y/anchor-is-valid, jsx-a11y/anchor-has-content
                        components={{ a: <a /> }}
                    />
                </div>
            </div>
        </>
    );
};

const AccountExistsSocial = ({
    isNativeAppContext,
    provider,
    redirectLink
}) => {
    const { toLocalizedUrl } = useUrlLocalizer();
    const { t } = useTranslator();
    return (
        <>
            <div />
            <div className={'mt-7'}>
                <Heading>{t('registration_sign_up_account_exists')}</Heading>
                <div className={'text-center mb-2'}>
                    {t('registration_sign_up_already_logged_in')}
                </div>
                <Button
                    noTransform
                    theme="primary"
                    fluid
                    className="my-3"
                    href={
                        isNativeAppContext
                            ? `tradera://login/${provider}`
                            : toLocalizedUrl(redirectLink)
                    }>
                    {isNativeAppContext
                        ? t('registration_sign_up_continue')
                        : t('registration_sign_up_go_to_tradera')}
                </Button>
                <div className={'text-center'}>
                    <Trans
                        i18nKey="registration_sign_up_problems_Trans"
                        // eslint-disable-next-line jsx-a11y/anchor-is-valid, jsx-a11y/anchor-has-content
                        components={{ a: <a /> }}
                    />
                </div>
            </div>
        </>
    );
};

const AccountApprovedSocial = ({
    isNativeAppContext,
    providerType,
    redirectLink
}) => {
    const { toLocalizedUrl } = useUrlLocalizer();
    const { t } = useTranslator();

    return (
        <>
            <div />
            <div className={'mt-7'}>
                <div className={'text-center'}>
                    <CheckBadgeIcon className="icon icon-xxl" />
                </div>
                <Heading>{t('registration_sign_up_thank_you')}</Heading>
                <div className={'mb-3'}>
                    {t('registration_sign_up_approved_body')}
                </div>
                <Button
                    noTransform
                    theme="primary"
                    fluid
                    className="my-3"
                    href={
                        isNativeAppContext
                            ? `tradera://login/${providerType}`
                            : toLocalizedUrl(redirectLink)
                    }>
                    {isNativeAppContext
                        ? t('registration_sign_up_continue')
                        : t('registration_account_created_to_tradera')}
                </Button>
            </div>
        </>
    );
};

const ManualReviewSocial = ({ redirectLink, emailAddress }) => {
    const { t } = useTranslator();
    return (
        <>
            <div />
            <FormCompleted
                redirectLink={redirectLink}
                emailAddress={emailAddress}
                accountCreatedInfoText={t(
                    'registration_fp_account_created_info'
                )}
                sendPasswordText={t(
                    'registration_fp_account_created_send_password'
                )}
                registrationProblemsText={
                    <Trans
                        i18nKey="registration_problems_Trans"
                        // eslint-disable-next-line jsx-a11y/anchor-is-valid, jsx-a11y/anchor-has-content
                        components={{ a: <a /> }}
                    />
                }
            />
        </>
    );
};

const AccountCreated = ({ redirectLink, emailAddress }) => {
    const { t } = useTranslator();
    return (
        <>
            <div />
            <FormCompleted
                redirectLink={redirectLink}
                emailAddress={emailAddress}
                accountCreatedInfoText={t(
                    'registration_fp_account_created_info'
                )}
                sendPasswordText={t(
                    'registration_fp_account_created_send_password'
                )}
                registrationProblemsText={
                    <Trans
                        i18nKey="registration_problems_Trans"
                        // eslint-disable-next-line jsx-a11y/anchor-is-valid, jsx-a11y/anchor-has-content
                        components={{ a: <a /> }}
                    />
                }
            />
        </>
    );
};
